import styled from "styled-components";

import { useEffect, useState, type FunctionComponent } from "react";
import { useMediaQuery } from "usehooks-ts";
import { Profanity } from '@2toad/profanity';
import { useMutation } from "@apollo/client";
import { getTheme, themed, Div, Grid, FlexStream, Flow, Stream, Text as Tx,  } from "@bracket/ui";
import { SaveFormBtn } from "./components";
import { FormFields } from "./components/FormFields";
import { UPDATE_PROFILE } from "@gql/mutations";
import { getAccount } from "wagmi/actions";
import { wagmiConfig } from "@wagmiconfig";
import { useAddNotification } from "@bracket/platform/store";



export declare type ValidationErrors = {
	name?: string;
	nickname?: string;
	email?: string;
};


export declare type FormProps = {
	userData: {
		name: string;
		nickname: string;
		email: string;
	};
	refetch: () => void;
}



export const Form: FunctionComponent<FormProps> = ({ userData, refetch, }) => {

	const isDesktop = useMediaQuery("(min-width: 768px)");
	const addNotification = useAddNotification();

	const { address, } = getAccount(wagmiConfig);
	const [initialData, setInitialData] = useState({ name: "", nickname: "", email: "", });
	const [formData, setFormData] = useState({ name: "", nickname: "", email: "", });
	const [isDirty, setIsDirty] = useState(false);

	const [errors, setErrors] = useState<ValidationErrors>({});

	useEffect(() => {
		setInitialData(userData);
		setFormData(userData);
	}, [
		userData,
	]);

	useEffect(() => {
		const hasChanges = Object.keys(formData).some(key => {
			const value = formData[key as keyof typeof formData];
			const initialValue = initialData[key as keyof typeof initialData];
			if(value === "" && initialValue === "")
				return false;
			return value !== initialValue;
		});
		setIsDirty(hasChanges);
	}, [
		formData,
		initialData,
	]);

	const [updateProfile, { loading }] = useMutation(UPDATE_PROFILE, {
		context: {
			headers: {
				wallet: address,
			},
		},
		onCompleted: () => {
			refetch();
		},
	});

	const validateForm = (data: typeof formData): ValidationErrors => {

		const profane = new Profanity();
		const newErrors: ValidationErrors = {};

		if(data.name) {
			if(!/^[a-zA-Z ]+$/.test(data.name)) {
				newErrors.name = "Name should contain only letters and spaces";
			} else if (data.name.length > 32) {
				newErrors.name = "Name should not exceed 32 characters";
			}
		}

		if(data.nickname) {
			if (!/^[a-zA-Z0-9]+$/.test(data.nickname)) {
				newErrors.nickname = "Nickname should contain only letters and numbers";
			} else if (data.nickname.length > 32) {
				newErrors.nickname = "Nickname should not exceed 32 characters";
			}
			if(profane.exists(data.nickname)) {
				newErrors.nickname = "Nickname contains profanity";
			}
		}

		if(data.email) {
			const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
			if (!emailRegex.test(data.email)) {
				newErrors.email = "Invalid email address";
			}
		}

		return newErrors;
	};

	const handleFieldBlur = (field: string, value: string) => {
		const fieldError = validateForm({ ...formData, [field]: value });
		if(fieldError[field as keyof ValidationErrors]) {
			setErrors((prev) => ({
				...prev,
				[field]: fieldError[field as keyof ValidationErrors],
			}));
		}
	};

	const handleFieldChange = (field: string, value: string) => {
		const newFormData = {
			...formData,
			[field]: value,
		};
		setFormData(newFormData);
		setErrors((prev) => {
			const newErrors = { ...prev };
			delete newErrors[field as keyof ValidationErrors];
			return newErrors;
		});
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const validationErrors = validateForm(formData);
		setErrors(validationErrors);

		if(Object.keys(validationErrors).length > 0) {
			return;
		}

		try {
			await updateProfile({
				variables: formData,
			});
			setInitialData(formData);
			setIsDirty(false);
			addNotification({
				title: "Profile Updated",
				message: "Your profile has been successfully updated",
				status: "success",
				type: "success",
				secondaryAction: {
					label: "Dismiss",
					onClick: () => console.log("Notification dismissed"),
				},
			});
		}
		catch(error) {

			if(error instanceof Error) {
				const errorMessage = error.message;
				if(errorMessage.includes("wallet cannot be authorized")) {
				} else if (errorMessage.includes("invalid email")) {
					setErrors((prev) => ({ ...prev, email: errorMessage }));
				} else if (
					errorMessage.includes("invalid nickname") ||
					errorMessage.includes("nickname already")
				) {
					setErrors((prev) => ({ ...prev, nickname: errorMessage }));
				} else if (errorMessage.includes("invalid name")) {
					setErrors((prev) => ({ ...prev, name: errorMessage }));
				}
			}

			addNotification({
				title: "Update Failed",
				message: "Failed to update profile. Please try again.",
				type: "error",
				status: "error",
				secondaryAction: {
					label: "Dismiss",
					onClick: () => console.log("Notification dismissed"),
				},
			});

		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<Container>
				<FormNameContainer>
					<Tx h5>Personal settings</Tx>
					{ isDesktop && ( <SaveFormBtn loading={loading} disabled={!isDirty || Object.keys(errors).length > 0} /> ) }
				</FormNameContainer>
				<FormFields
					formData={formData}
					onFieldChange={handleFieldChange}
					onFieldBlur={handleFieldBlur}
					errors={errors}
				/>
			</Container>

			{!isDesktop && (
				<>
					<Divider />
					<SaveBtnContainer>
						<SaveFormBtn
							loading={loading}
							disabled={!isDirty || Object.keys(errors).length > 0}
						/>
					</SaveBtnContainer>
				</>
			)}
		</form>
	);
};




const Container = styled(Grid)`
	display: grid;
	grid-template-columns: 1fr;
	padding: 32px;
	gap: 32px;
	@media (min-width: 768px) {
		padding: 16px 32px 64px;
	}
`;

const FormNameContainer = styled(FlexStream)`
	justify-content: space-between;
	align-items: center;
`;

const Divider = styled(Div)`
	border-bottom: 1px solid #242424;
`;

const SaveBtnContainer = styled(Flow)`
	display: grid;
	padding: 20px 12px;
`;
